import styled from 'styled-components';
import { SidebarMenuSection } from '@ardoq/sidebar-menu';
import { APIFieldAttributes, ArdoqId } from '@ardoq/api-types';
import { caption, InlineText } from '@ardoq/typography';
import { ExternalLink } from '@ardoq/link';
import { GhostButton, TertiaryButton } from '@ardoq/button';
import { dispatchAction } from '@ardoq/rxbeach';
import { recalculateWorkspaceField } from 'admin/workspaceOverview/actions';
import { colors, s16 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Field } from '@ardoq/forms';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { CalculatedFieldEventStatus, CalculatedFieldEventMap } from './types';
import { ErrorNotification } from '@ardoq/status-ui';
import { getCalculatedFieldErrorEvents } from './utils';
import { Icon, IconName, IconSize, SpinnerIcon } from '@ardoq/icons';
import { formatDateTimeShort } from '@ardoq/date-time';
import { type Locale } from '@ardoq/locale';
import { t, type I18nLocale } from '@ardoq/i18n';

const CalculatedField = styled(FlexBox).attrs({
  gap: 'small',
  flexDirection: 'column',
})`
  padding: 0 ${s16} ${s16} ${s16};
  border-bottom: 1px solid ${colors.grey300};
`;

const ErrorNotificationContainer = styled.div`
  padding: 0 ${s16};
`;

const LastCalculation = styled.div`
  ${caption};
  color: ${colors.grey500};
`;

const InfoMessageContainer = styled.div`
  max-width: 304px;
`;

const RecalculatedTag = styled(FlexBox)`
  color: ${colors.green900};
  ${caption};
`;

const getInfoMessage = (i18nLocale: I18nLocale) => () => (
  <InfoMessageContainer>
    <p>
      {t(
        i18nLocale,
        'menus:calculatedFields.info.description',
        'Calculated fields automatically process information across your workspaces and store the result as a field value. All calculations are scheduled to run hourly.'
      )}
    </p>

    <p>
      {t(
        i18nLocale,
        'menus:calculatedFields.info.readMore',
        'Read more about this on our '
      )}
      <InlineText variant="text2">
        <ExternalLink
          opensInNewTabLabel={t(
            i18nLocale,
            'common:labels.opensInNewTab',
            '(new tab)'
          )}
          href={KnowledgeBaseLink.CALCULATED_FIELDS}
        >
          {t(
            i18nLocale,
            'menus:calculatedFields.info.knowledgeBase',
            'Knowledge Base'
          )}
        </ExternalLink>
      </InlineText>
    </p>
  </InfoMessageContainer>
);

interface CalculatedFieldSectionProps {
  calculatedFields: APIFieldAttributes[];
  fieldStatusMap: CalculatedFieldEventMap;
  branchId?: ArdoqId | null;
  locale: Locale;
  i18nLocale: I18nLocale;
}

export const CalculatedFieldSection = ({
  calculatedFields,
  fieldStatusMap,
  branchId,
  locale,
  i18nLocale,
}: CalculatedFieldSectionProps) => {
  const errorEvents = getCalculatedFieldErrorEvents(fieldStatusMap);

  return (
    <SidebarMenuSection
      title={t(i18nLocale, 'menus:calculatedFields.title', 'Calculated fields')}
      isCollapsible={false}
      infoPopover={getInfoMessage(i18nLocale)}
    >
      <div
        style={{ display: 'contents' }}
        role="list"
        aria-label={t(
          i18nLocale,
          'menus:calculatedFields.list',
          'Calculated fields list'
        )}
      >
        {Object.keys(errorEvents).length > 1 ? (
          <ErrorNotificationContainer>
            <ErrorNotification>
              {t(
                i18nLocale,
                'menus:calculatedFields.fieldsFailed',
                { count: Object.keys(errorEvents).length },
                '{{count}} fields failed to recalculate. See the errors of the field level to resolve them.'
              )}
            </ErrorNotification>
          </ErrorNotificationContainer>
        ) : null}
        {calculatedFields.map(({ _id, label, calculatedFieldExtras }) => (
          <CalculatedField key={_id} role="listitem" aria-label={label}>
            <Field label={label}>
              {fieldStatusMap[_id]?.status ===
              CalculatedFieldEventStatus.CALCULATING ? (
                <GhostButton isActive>
                  {t(
                    i18nLocale,
                    'menus:calculatedFields.calculating',
                    'Calculating'
                  )}{' '}
                  <SpinnerIcon />
                </GhostButton>
              ) : (
                <TertiaryButton
                  onClick={() => {
                    dispatchAction(
                      recalculateWorkspaceField({
                        fieldId: _id,
                        branchId,
                      })
                    );
                  }}
                >
                  {t(
                    i18nLocale,
                    'menus:calculatedFields.recalculate',
                    'Recalculate'
                  )}
                </TertiaryButton>
              )}
            </Field>

            <FlexBox justify="space-between" gap="medium">
              <LastCalculation>
                {t(
                  i18nLocale,
                  'menus:calculatedFields.lastCalculation',
                  {
                    value: calculatedFieldExtras?.lastRecalculation
                      ? formatDateTimeShort(
                          calculatedFieldExtras?.lastRecalculation,
                          locale
                        )
                      : t(i18nLocale, 'menus:calculatedFields.never', 'Never'),
                  },
                  'Last calculation: {{value}}'
                )}
              </LastCalculation>

              {fieldStatusMap[_id]?.status ===
                CalculatedFieldEventStatus.DONE ||
              fieldStatusMap[_id]?.status ===
                CalculatedFieldEventStatus.DONE_NOTIFIED ? (
                <RecalculatedTag
                  justify="space-between"
                  gap="small"
                  align="center"
                >
                  {t(
                    i18nLocale,
                    'menus:calculatedFields.recalculated',
                    'Recalculated'
                  )}
                  <Icon
                    iconName={IconName.CHECK_CIRCLE}
                    iconSize={IconSize.SMALL}
                    style={{ color: colors.green600 }}
                  />
                </RecalculatedTag>
              ) : null}
            </FlexBox>

            {errorEvents[_id] ? (
              <FlexBox
                align="center"
                style={{ color: colors.red700 }}
                gap="small"
              >
                <Icon iconName={IconName.ERROR} iconSize={IconSize.SMALL} />
                {errorEvents[_id].status.message}
              </FlexBox>
            ) : null}
          </CalculatedField>
        ))}
      </div>
    </SidebarMenuSection>
  );
};
